import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Users, FileText, Briefcase, Newspaper, ArrowRight, Menu, X, Phone, MapPin, Mail, ChevronRight, ExternalLink, Facebook, Instagram, Shield, Cookie, Scale, Heart, Zap, Handshake, Calculator, MessageCircle, Lock, ChevronUp, Eye } from "lucide-react";
import axios from "axios";

const UGL_LOGO = "/ugl-logo.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_NEWS = [
  { titolo: "Presentazione libro Giuseppe Mosa: Diritti al cuore dell'impresa", contenuto: "Roma, 26 marzo 2026 - Universita degli Studi di Roma La Sapienza.", data: "26 Mar 2026", link: "https://www.ugl.it/presentazione-libro-giuseppe-mosa-diritti-al-cuore-dellimpresa-profili-della-partecipazione-dei-lavoratori-alla-gestione-delle-imprese/", img: "https://www.ugl.it/wp-content/uploads/2026/03/diritto-Banner-sito-400x250.jpg" },
  { titolo: "UGL, Capone: Celebriamo una lunga storia di battaglie", contenuto: "I 76 anni della UGL, nata il 24 marzo 1950.", data: "24 Mar 2026", link: "https://www.ugl.it/ugl-capone-celebriamo-storia-di-battaglie-e-impegno-al-servizio-lavoratori/", img: "https://www.ugl.it/wp-content/uploads/2026/03/76%C2%B0-anniversario-UGL-2026-03-400x250.jpg" },
  { titolo: "UGL: il patrimonio documentale entra nell'archivio centrale dello stato", contenuto: "Iniziativa 'La nostra storia in un archivio' per il 76 anniversario.", data: "24 Mar 2026", link: "https://www.ugl.it/ugl-il-patrimonio-documentale-entra-nellarchivio-centrale-dello-stato/", img: "https://www.ugl.it/wp-content/uploads/2025/01/UGL-75-Anni-copia-1-300x300.png" },
];

const navLinks = [
  { label: "Home", href: "#home" },
  { label: "Servizi", href: "#servizi" },
  { label: "I Tuoi Diritti", href: "#diritti" },
  { label: "Chi Siamo", href: "#chi-siamo" },
  { label: "Notizie", href: "#notizie" },
  { label: "Tesseramento", href: "/tesseramento", isRoute: true },
  { label: "Strumenti", href: "/strumenti", isRoute: true },
  { label: "Contatti", href: "#contatti" },
];

function scrollTo(id) {
  const el = document.getElementById(id.replace("#", ""));
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [pageViews, setPageViews] = useState(0);
  const [cookieAccepted, setCookieAccepted] = useState(() => localStorage.getItem('ugl_cookies') === 'accepted');
  const [uglNews, setUglNews] = useState(FALLBACK_NEWS);
  const heroRef = useRef(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [serviceModal, setServiceModal] = useState(null);

  const SERVICES_DATA = [
    { icon: Calculator, title: "CAF - Centro Assistenza Fiscale", desc: "Dichiarazione dei redditi, ISEE, 730, RED, bonus e agevolazioni fiscali per i lavoratori.", gold: true, detail: "Il nostro Centro di Assistenza Fiscale offre un servizio completo per tutti gli adempimenti fiscali.", items: ["Modello 730 e Redditi PF", "Calcolo e compilazione ISEE/DSU", "Modello RED e INVCIV", "Bonus sociali (luce, gas, acqua)", "IMU e tributi locali", "Successioni e volture catastali", "Contratti di locazione e cedolare secca", "Detrazioni e agevolazioni fiscali"], cta: "Prenota appuntamento CAF" },
    { icon: Shield, title: "Patronato", desc: "Assistenza per pensioni, disoccupazione, maternità, invalidità, infortuni e malattie professionali.", gold: false, detail: "Il Patronato UGL Lariano assiste gratuitamente i lavoratori in tutte le pratiche previdenziali e assistenziali.", items: ["Domanda di pensione (vecchiaia, anticipata)", "NASpI - indennità di disoccupazione", "Maternità e congedi parentali", "Invalidità civile e accompagnamento", "Infortuni e malattie professionali", "Assegno Unico Universale", "Permessi Legge 104", "Riscatto laurea e ricongiunzione"], cta: "Contatta il Patronato" },
    { icon: Scale, title: "Avvocato del Lavoro", desc: "Consulenza legale specializzata in diritto del lavoro, licenziamenti, mobbing e contenziosi.", gold: true, detail: "Studio legale convenzionato con consulenza specializzata in diritto del lavoro a tariffe agevolate per gli iscritti.", items: ["Licenziamenti illegittimi e impugnazione", "Mobbing e straining", "Differenze retributive e straordinari", "Demansionamento e trasferimenti", "Vertenze e conciliazioni", "Contratti atipici", "Discriminazioni e molestie", "Licenziamenti collettivi"], cta: "Richiedi consulenza legale" },
    { icon: Heart, title: "Medico Legale", desc: "Perizie medico-legali, visite di idoneità, riconoscimento invalidità e infortuni sul lavoro.", gold: false, detail: "Servizio di medicina legale per perizie e valutazioni a tutela della salute del lavoratore.", items: ["Perizie per invalidità civile", "Valutazione danno biologico", "Visite idoneità lavorativa", "Perizie infortuni sul lavoro", "Certificazioni Legge 104", "Ricorsi contro verbali ASL/INPS", "Valutazione malattie professionali", "Assistenza commissioni mediche"], cta: "Prenota visita medico-legale" },
    { icon: Zap, title: "Tariffe Luce e Gas", desc: "Tariffe speciali e convenzioni esclusive per contratti energia elettrica e gas naturale.", gold: true, detail: "Grazie alle convenzioni UGL, gli iscritti accedono a tariffe agevolate per le utenze domestiche.", items: ["Tariffe luce a prezzo bloccato", "Tariffe gas scontate", "Confronto offerte mercato libero", "Assistenza cambio fornitore", "Verifica bollette e conguagli", "Bonus sociale automatico", "Volture e subentri", "Conciliazioni ARERA"], cta: "Richiedi preventivo energia" },
    { icon: Handshake, title: "Convenzioni", desc: "Sconti e agevolazioni con attività commerciali, assicurazioni, servizi sanitari e molto altro.", gold: false, detail: "La tessera UGL Lariano dà accesso a una rete di convenzioni con attività e professionisti del territorio.", items: ["Assicurazioni auto/casa scontate", "Visite mediche e dentistiche", "Sconti negozi locali", "Palestre e centri sportivi", "Convenzioni ottici e farmacia", "Noleggio auto agevolato", "Sconti formazione e corsi", "Agevolazioni viaggi"], cta: "Scopri le convenzioni" },
  ];

  useEffect(() => {
    const onScroll = () => { setScrolled(window.scrollY > 20); setShowScrollTop(window.scrollY > 400); };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    axios.post(`${API}/page-views/increment`).then(r => setPageViews(r.data.views)).catch(() => {});
  }, []);

  const handleMouse = useCallback((e) => {
    if (!heroRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    setMouse({
      x: ((e.clientX - rect.left) / rect.width - 0.5) * 2,
      y: ((e.clientY - rect.top) / rect.height - 0.5) * 2,
    });
  }, []);

  const acceptCookies = () => { localStorage.setItem('ugl_cookies', 'accepted'); setCookieAccepted(true); };

  useEffect(() => {
    axios.get(`${API}/ugl-news`).then(r => {
      if (r.data && r.data.length > 0) setUglNews(r.data.slice(0, 6));
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" id="home">
      {/* Skip to Content - Accessibilità */}
      <a href="#main-content" className="skip-link" data-testid="skip-link">Vai al contenuto principale</a>
      {/* Header + Menu */}
      <header role="banner" aria-label="Navigazione principale" className={`glass-header sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 shadow-md border-b border-slate-200' : 'bg-white/80 border-b border-slate-200'}`} data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 flex items-center justify-between h-16">
          {/* Logo */}
          <button onClick={() => scrollTo("#home")} className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid="nav-logo" aria-label="Torna alla home">
            <img src={UGL_LOGO} alt="Logo UGL - Unione Zonale Lariano" className="w-10 h-10 object-contain" />
            <span className="font-semibold text-[#003366] text-lg tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              UGL Lariano
            </span>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav" role="navigation" aria-label="Menu principale">
            {navLinks.map(link => link.isRoute ? (
              <Link key={link.href} to={link.href} data-testid={`nav-link-${link.label.toLowerCase()}`}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-[#003366] hover:bg-[#E0F2FE]/50 rounded-md transition-all duration-200">
                {link.label}
              </Link>
            ) : (
              <button key={link.href} data-testid={`nav-link-${link.label.toLowerCase()}`}
                onClick={() => scrollTo(link.href)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-[#003366] hover:bg-[#E0F2FE]/50 rounded-md transition-all duration-200">
                {link.label}
              </button>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link to="/login">
              <Button data-testid="header-login-btn" className="bg-[#003366] text-white hover:bg-[#003366]/90 px-6 rounded-md font-medium">
                Accedi
              </Button>
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            data-testid="mobile-hamburger-btn"
            className="md:hidden p-2 rounded-md hover:bg-slate-100 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Chiudi menu" : "Apri menu"}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="w-5 h-5 text-[#003366]" /> : <Menu className="w-5 h-5 text-[#003366]" />}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white animate-slide-up" data-testid="mobile-nav-menu">
            <div className="px-6 py-4 space-y-1">
              {navLinks.map(link => link.isRoute ? (
                <Link key={link.href} to={link.href} onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-slate-700 hover:text-[#003366] hover:bg-[#E0F2FE]/40 rounded-md transition-all">
                  {link.label} <ChevronRight className="w-4 h-4 text-slate-300" />
                </Link>
              ) : (
                <button key={link.href} data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  onClick={() => { scrollTo(link.href); setMobileMenuOpen(false); }}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-slate-700 hover:text-[#003366] hover:bg-[#E0F2FE]/40 rounded-md transition-all">
                  {link.label} <ChevronRight className="w-4 h-4 text-slate-300" />
                </button>
              ))}
              <div className="pt-3 border-t border-slate-100">
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button data-testid="mobile-login-btn" className="w-full bg-[#003366] text-white hover:bg-[#003366]/90 rounded-md font-medium">
                    Accedi al Portale
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Hero - Digitale e Antico */}
      <section id="main-content" ref={heroRef} onMouseMove={handleMouse} className="hero-section relative overflow-hidden text-white" data-testid="hero-section" role="main" aria-label="Presentazione UGL Lariano">
        {/* Animated Grid Background */}
        <div className="absolute inset-0 hero-bg-deep">
          <svg className="absolute inset-0 w-full h-full opacity-[0.07]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 60" fill="none" stroke="white" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
          {/* Floating particles */}
          {[...Array(18)].map((_, i) => (
            <div key={i} className="particle" aria-hidden="true" style={{
              left: `${5 + (i * 5.5) % 95}%`,
              top: `${10 + (i * 7.3) % 80}%`,
              animationDelay: `${i * 0.7}s`,
              animationDuration: `${6 + (i % 4) * 2}s`,
              width: `${3 + (i % 3) * 2}px`,
              height: `${3 + (i % 3) * 2}px`,
            }} />
          ))}
          {/* Ornamental corner lines */}
          <div className="absolute top-8 left-8 w-24 h-24 border-t-2 border-l-2 border-[#C9A84C]/30 hidden lg:block" />
          <div className="absolute bottom-8 right-8 w-24 h-24 border-b-2 border-r-2 border-[#C9A84C]/30 hidden lg:block" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 animate-slide-up">
              {/* Vintage Badge */}
              <div className="inline-flex items-center gap-3 mb-6">
                <div className="vintage-seal w-14 h-14 rounded-full border-2 border-[#C9A84C] flex items-center justify-center relative">
                  <span className="text-[#C9A84C] text-xs font-bold tracking-widest" style={{ fontFamily: 'Georgia, serif' }}>1950</span>
                  <div className="absolute inset-0 rounded-full border border-[#C9A84C]/40 scale-[1.15]" />
                </div>
                <div>
                  <p className="text-[10px] font-semibold tracking-[0.25em] uppercase text-[#C9A84C]">Fondato nel 1950</p>
                  <p className="text-xs tracking-[0.15em] uppercase text-[#00A0D1]">UGL - Unione Zonale Lariano</p>
                </div>
              </div>

              {/* Main Heading with ornament */}
              <div className="relative">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Il lavoro al centro
                </h1>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  della <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00A0D1] to-[#C9A84C]">comunità</span>
                </h1>
                {/* Ornamental divider */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-px w-12 bg-[#C9A84C]/60" />
                  <div className="w-2 h-2 rotate-45 border border-[#C9A84C]/60" />
                  <div className="h-px w-24 bg-[#C9A84C]/40" />
                </div>
              </div>

              <p className="text-base sm:text-lg text-white/70 leading-relaxed mb-8 max-w-lg" style={{ fontFamily: "'Georgia', serif" }}>
                Una piattaforma interattiva per gli iscritti del sindacato. 
                Partecipa ai bandi, crea progetti, costruisci la tua rete 
                professionale e gestisci il tuo curriculum vitae.
              </p>

              <div className="flex flex-wrap gap-4 mb-10">
                <Link to="/login">
                  <Button data-testid="hero-accedi-btn" size="lg" className="bg-white text-[#003366] hover:bg-white/90 px-8 rounded-md font-medium shadow-lg shadow-black/20 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200">
                    Accedi al Portale <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <button onClick={() => scrollTo("#servizi")}>
                  <Button data-testid="hero-scopri-btn" size="lg" variant="outline" className="border-2 border-[#C9A84C]/50 text-[#C9A84C] hover:bg-[#C9A84C]/10 hover:border-[#C9A84C] px-8 rounded-md font-medium transition-all duration-200">
                    Scopri i Servizi
                  </Button>
                </button>
              </div>

              {/* Animated Stats Bar */}
              <div className="flex flex-wrap items-center gap-6 lg:gap-10">
                {[
                  { value: "76+", label: "Anni di Storia" },
                  { value: "1000+", label: "Lavoratori Assistiti" },
                  { value: "100%", label: "Impegno Quotidiano" },
                ].map((stat, i) => (
                  <div key={i} className={`animate-slide-up stagger-${i + 2}`} style={{ opacity: 0, animationFillMode: 'forwards' }}>
                    <p className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{stat.value}</p>
                    <p className="text-[10px] font-semibold tracking-[0.15em] uppercase text-white/40">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side - Interactive Network Visualization */}
            <div className="lg:col-span-5 hidden lg:flex items-center justify-center relative">
              <div className="network-container relative w-[400px] h-[400px]" style={{ transform: `translate(${mouse.x * 12}px, ${mouse.y * 8}px)`, transition: 'transform 0.15s ease-out' }}>
                {/* Network SVG */}
                <svg viewBox="0 0 400 400" className="w-full h-full absolute inset-0">
                  {/* Connection lines */}
                  <line x1="200" y1="60" x2="320" y2="140" className="network-line" stroke="#00A0D1" strokeWidth="1" opacity="0.3" />
                  <line x1="200" y1="60" x2="100" y2="160" className="network-line" stroke="#C9A84C" strokeWidth="1" opacity="0.25" />
                  <line x1="320" y1="140" x2="340" y2="260" className="network-line" stroke="#00A0D1" strokeWidth="1" opacity="0.3" />
                  <line x1="100" y1="160" x2="80" y2="280" className="network-line" stroke="#00A0D1" strokeWidth="1" opacity="0.25" />
                  <line x1="100" y1="160" x2="200" y2="200" className="network-line" stroke="#C9A84C" strokeWidth="1" opacity="0.2" />
                  <line x1="320" y1="140" x2="200" y2="200" className="network-line" stroke="#00A0D1" strokeWidth="1" opacity="0.25" />
                  <line x1="200" y1="200" x2="80" y2="280" className="network-line" stroke="#00A0D1" strokeWidth="1" opacity="0.2" />
                  <line x1="200" y1="200" x2="340" y2="260" className="network-line" stroke="#C9A84C" strokeWidth="1" opacity="0.2" />
                  <line x1="200" y1="200" x2="200" y2="340" className="network-line" stroke="#00A0D1" strokeWidth="1" opacity="0.3" />
                  <line x1="80" y1="280" x2="200" y2="340" className="network-line" stroke="#C9A84C" strokeWidth="1" opacity="0.2" />
                  <line x1="340" y1="260" x2="200" y2="340" className="network-line" stroke="#00A0D1" strokeWidth="1" opacity="0.25" />
                </svg>

                {/* Network Nodes */}
                {[
                  { x: 200, y: 60, label: "Lavoro", size: "lg", delay: 0 },
                  { x: 320, y: 140, label: "Bandi", size: "md", delay: 0.3 },
                  { x: 100, y: 160, label: "Diritti", size: "md", delay: 0.5 },
                  { x: 200, y: 200, label: "UGL", size: "xl", delay: 0.1, gold: true },
                  { x: 80, y: 280, label: "Rete", size: "sm", delay: 0.7 },
                  { x: 340, y: 260, label: "Progetti", size: "md", delay: 0.4 },
                  { x: 200, y: 340, label: "Comunità", size: "lg", delay: 0.6 },
                ].map((node, i) => (
                  <div
                    key={i}
                    className={`network-node absolute flex items-center justify-center rounded-full ${node.gold ? 'border-[#C9A84C]' : 'border-[#00A0D1]/60'} border backdrop-blur-sm`}
                    style={{
                      left: `${(node.x / 400) * 100}%`,
                      top: `${(node.y / 400) * 100}%`,
                      transform: 'translate(-50%, -50%)',
                      width: node.size === 'xl' ? 80 : node.size === 'lg' ? 60 : node.size === 'md' ? 50 : 40,
                      height: node.size === 'xl' ? 80 : node.size === 'lg' ? 60 : node.size === 'md' ? 50 : 40,
                      background: node.gold ? 'rgba(201,168,76,0.15)' : 'rgba(0,160,209,0.1)',
                      animationDelay: `${node.delay}s`,
                    }}
                  >
                    <span className={`text-[9px] font-bold tracking-wider uppercase ${node.gold ? 'text-[#C9A84C]' : 'text-white/70'}`}>
                      {node.label}
                    </span>
                    {node.gold && <div className="absolute inset-0 rounded-full border border-[#C9A84C]/30 animate-ping-slow" />}
                  </div>
                ))}

                {/* Vintage decorative ring */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-[280px] h-[280px] rounded-full border border-dashed border-[#C9A84C]/15 vintage-rotate" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom news ticker */}
        <div className="relative z-10 border-t border-white/10 bg-black/20 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-3 flex items-center gap-4 overflow-hidden">
            <span className="text-[10px] font-bold tracking-widest uppercase text-[#C9A84C] flex-shrink-0">Ultime</span>
            <div className="news-ticker flex-1 overflow-hidden">
              <div className="news-ticker-inner whitespace-nowrap">
                {uglNews.map((n, i) => (
                  <a key={i} href={n.link} target="_blank" rel="noopener noreferrer" className="inline-block mx-8 text-xs text-white/50 hover:text-white/80 transition-colors">
                    {n.data || ""} {n.data ? "—" : ""} {n.titolo}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services - Dark Theme */}
      <section id="servizi" className="section-dark relative overflow-hidden py-20 lg:py-28" data-testid="services-section" aria-label="I nostri servizi">
        <div className="section-grid-bg" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12 bg-[#C9A84C]/40" />
              <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#C9A84C]">I Nostri Servizi</p>
              <div className="h-px w-12 bg-[#C9A84C]/40" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Tutto per il lavoratore
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              ...SERVICES_DATA,
              { icon: FileText, title: "Bandi e Opportunità", desc: "Partecipa ai bandi pubblicati dal sindacato con un solo click dalla tua area personale.", gold: true },
              { icon: Briefcase, title: "Progetti Sindacali", desc: "Collabora a progetti della sezione e costruisci la tua esperienza professionale.", gold: false },
              { icon: Users, title: "Rete Professionale", desc: "Connettiti con altri iscritti e costruisci la tua rete del lavoro e delle competenze.", gold: true },
              { icon: FileText, title: "Curriculum Vitae", desc: "Il tuo CV sempre aggiornato e accessibile nella tua area personale del portale.", gold: false },
              { icon: Newspaper, title: "Notizie e Aggiornamenti", desc: "Resta informato sulle novità del sindacato e del mondo del lavoro.", gold: true },
              { icon: Shield, title: "Tutela dei Diritti", desc: "Assistenza e supporto completo per la tutela dei tuoi diritti lavorativi.", gold: false },
            ].map((s, i) => (
              <div key={i} onClick={() => s.detail ? setServiceModal(s) : null}
                className={`glass-card group p-6 rounded-lg border animate-slide-up ${s.detail ? 'cursor-pointer' : ''} ${s.gold ? 'border-[#C9A84C]/20 hover:border-[#C9A84C]/40' : 'border-white/10 hover:border-[#00A0D1]/30'}`} style={{ opacity: 0, animationFillMode: 'forwards', animationDelay: `${i * 0.1}s` }}>
                <div className={`w-11 h-11 rounded-md flex items-center justify-center mb-4 ${s.gold ? 'bg-[#C9A84C]/10' : 'bg-[#00A0D1]/10'}`}>
                  <s.icon className={`w-5 h-5 ${s.gold ? 'text-[#C9A84C]' : 'text-[#00A0D1]'}`} />
                </div>
                <h3 className="text-base font-semibold text-white mb-2 group-hover:text-[#00A0D1] transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.title}</h3>
                <p className="text-sm text-white/50 leading-relaxed">{s.desc}</p>
                {s.detail && <p className="text-[10px] text-[#C9A84C]/60 mt-3 font-semibold uppercase tracking-wider">Clicca per dettagli →</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Chiedi il Tuo Diritto - Sede Protetta */}
      <section id="diritti" className="section-dark-alt relative overflow-hidden py-16 lg:py-24" data-testid="diritti-section" aria-label="Chiedi il tuo diritto - Sede protetta">
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12 bg-[#C9A84C]/40" />
              <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#C9A84C]">Sede Protetta</p>
              <div className="h-px w-12 bg-[#C9A84C]/40" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Chiedi il Tuo <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00A0D1] to-[#C9A84C]">Diritto</span>
            </h2>
            <p className="text-sm text-white/40 mt-3 max-w-2xl mx-auto" style={{ fontFamily: "'Georgia', serif" }}>
              La nostra sede in Via Roma 119, Lariano è riconosciuta come <strong className="text-white/60">sede protetta</strong> ai sensi 
              del diritto del lavoro italiano per la stipula di accordi e conciliazioni.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Scale, title: "Conciliazioni", desc: "Risolvi le controversie lavorative in sede protetta con valore legale. Assistenza completa nella mediazione." },
              { icon: Shield, title: "Vertenze", desc: "Avvio e gestione vertenze di lavoro per licenziamenti illegittimi, mancati pagamenti e demansionamento." },
              { icon: FileText, title: "Conteggi e Controlli", desc: "Verifica buste paga, calcolo TFR, controllo contributi INPS e conteggi differenze retributive." },
              { icon: Scale, title: "Valutazione Diritti", desc: "Analisi gratuita della tua situazione lavorativa. Valutiamo i tuoi diritti e le azioni possibili." },
              { icon: Heart, title: "Infortuni e Malattie", desc: "Assistenza per riconoscimento infortuni sul lavoro, malattie professionali e invalidità." },
              { icon: Shield, title: "Tutela Lavoratori", desc: "Protezione da mobbing, discriminazioni, licenziamenti ingiusti e violazioni contrattuali." },
            ].map((s, i) => (
              <div key={i} className="bg-[#13243d] border border-[#1e3456] rounded-xl p-5 hover:border-[#C9A84C]/30 hover:shadow-lg hover:shadow-black/20 transition-all group">
                <s.icon className="w-6 h-6 text-[#C9A84C] mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="text-sm font-semibold text-white mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.title}</h3>
                <p className="text-xs text-white/40 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <a href="https://wa.me/393514134225?text=Salve%2C%20vorrei%20una%20consulenza%20sui%20miei%20diritti%20lavorativi" target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#C9A84C] text-[#001830] hover:bg-[#C9A84C]/90 px-8 font-medium" data-testid="chiedi-diritto-btn">
                <MessageCircle className="w-4 h-4 mr-2" /> Chiedi una Consulenza Gratuita
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Chi Siamo - Dark Theme */}
      <section id="chi-siamo" className="section-dark-alt relative overflow-hidden py-20 lg:py-28" data-testid="about-section" aria-label="Chi siamo">
        <div className="absolute top-8 right-8 w-32 h-32 border-t-2 border-r-2 border-[#C9A84C]/15 hidden lg:block" />
        <div className="absolute bottom-8 left-8 w-32 h-32 border-b-2 border-l-2 border-[#C9A84C]/15 hidden lg:block" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -top-3 -left-3 w-full h-full border border-[#C9A84C]/20 rounded-lg" />
              <img
                src="https://images.pexels.com/photos/4963359/pexels-photo-4963359.jpeg"
                alt="Professional trust and agreement"
                className="relative rounded-lg shadow-2xl shadow-black/40 object-cover w-full h-[360px]"
              />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px w-8 bg-[#C9A84C]/60" />
                <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#C9A84C]">Chi Siamo</p>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-white tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                UGL Sezione Lariano
              </h2>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-12 bg-[#C9A84C]/40" />
                <div className="w-2 h-2 rotate-45 border border-[#C9A84C]/40" />
                <div className="h-px w-20 bg-[#C9A84C]/30" />
              </div>
              <p className="text-base text-white/60 leading-relaxed mb-4" style={{ fontFamily: "'Georgia', serif" }}>
                L'UGL - Unione Zonale Lariano, è al servizio dei lavoratori 
                e dei cittadini. La nostra missione è tutelare i diritti, creare opportunità 
                e costruire una rete solida per il futuro professionale di ogni iscritto.
              </p>
              <p className="text-base text-white/60 leading-relaxed" style={{ fontFamily: "'Georgia', serif" }}>
                Attraverso questa piattaforma digitale, offriamo strumenti moderni per la 
                partecipazione attiva ai bandi, ai progetti e alla vita sindacale.
              </p>
              <p className="text-base text-white/60 leading-relaxed mt-3" style={{ fontFamily: "'Georgia', serif" }}>
                <span className="text-[#C9A84C] font-semibold">Sede Protetta</span> - La nostra sede sindacale in Via Roma 119, Lariano è riconosciuta 
                come sede protetta ai sensi del diritto del lavoro italiano per la stipula di accordi e conciliazioni.
              </p>
              <div className="mt-8 flex items-center gap-6">
                <div className="vintage-seal w-14 h-14 rounded-full border-2 border-[#C9A84C] flex items-center justify-center">
                  <span className="text-[#C9A84C] text-xs font-bold" style={{ fontFamily: 'Georgia, serif' }}>76</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Anni di Esperienza</p>
                  <p className="text-xs text-white/40">Dal 1950 al fianco dei lavoratori</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Notizie - Dark Theme */}
      <section id="notizie" className="section-dark relative overflow-hidden py-20 lg:py-28" data-testid="news-section" aria-label="Ultime notizie">
        <div className="section-grid-bg" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12 bg-[#C9A84C]/40" />
              <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#C9A84C]">Ultime Notizie</p>
              <div className="h-px w-12 bg-[#C9A84C]/40" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Novità dal Sindacato
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {uglNews.map((n, i) => (
              <a key={i} href={n.link} target="_blank" rel="noopener noreferrer" className="block group">
                <div className="glass-card rounded-lg border border-white/10 overflow-hidden hover:border-[#00A0D1]/30 transition-all duration-300 h-full animate-slide-up" style={{ opacity: 0, animationFillMode: 'forwards', animationDelay: `${i * 0.1}s` }}>
                  {n.img && (
                    <div className="h-40 overflow-hidden">
                      <img src={n.img} alt={n.titolo} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className={`p-5 flex flex-col ${n.img ? '' : 'h-full'}`}>
                    <p className="text-[10px] font-semibold tracking-wider uppercase text-[#C9A84C] mb-2">{n.data}</p>
                    <h3 className="text-sm font-semibold text-white mb-2 group-hover:text-[#00A0D1] transition-colors leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>{n.titolo}</h3>
                    <p className="text-xs text-white/40 leading-relaxed line-clamp-3 flex-1">{n.contenuto}</p>
                    <div className="flex items-center gap-1.5 mt-3 text-xs font-medium text-[#00A0D1] group-hover:text-[#C9A84C] transition-colors">
                      Leggi su ugl.it <ExternalLink className="w-3 h-3" />
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Footer - Dark Theme with Social, Privacy, Cookie */}
      <footer id="contatti" className="section-footer relative overflow-hidden" data-testid="landing-footer" role="contentinfo" aria-label="Footer e contatti">
        {/* Top border ornament */}
        <div className="h-px bg-gradient-to-r from-transparent via-[#C9A84C]/30 to-transparent" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src={UGL_LOGO} alt="UGL" className="w-10 h-10 object-contain" />
                <span className="font-semibold text-lg text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>UGL Lariano</span>
              </div>
              <p className="text-sm text-white/40 leading-relaxed mb-5" style={{ fontFamily: "'Georgia', serif" }}>
                UGL - Unione Zonale Lariano. Al servizio dei lavoratori e dei cittadini dal 1950.
              </p>
              {/* Social Icons */}
              <div className="flex items-center gap-3">
                <a href="https://www.facebook.com/ugllariano" target="_blank" rel="noopener noreferrer" data-testid="social-facebook" aria-label="Seguici su Facebook" className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-[#1877F2] hover:border-[#1877F2] transition-all duration-300 group">
                  <Facebook className="w-4 h-4 text-white/50 group-hover:text-white" aria-hidden="true" />
                </a>
                <a href="https://www.instagram.com/ugl_lariano" target="_blank" rel="noopener noreferrer" data-testid="social-instagram" aria-label="Seguici su Instagram" className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-gradient-to-br hover:from-[#f09433] hover:via-[#e6683c] hover:to-[#bc1888] hover:border-transparent transition-all duration-300 group">
                  <Instagram className="w-4 h-4 text-white/50 group-hover:text-white" aria-hidden="true" />
                </a>
              </div>
            </div>

            {/* Contatti */}
            <div>
              <h4 className="font-medium text-xs uppercase tracking-[0.2em] text-[#C9A84C] mb-5">Contatti</h4>
              <div className="space-y-3">
                <a href="mailto:info@ugllariano.it" className="flex items-center gap-2.5 text-sm text-white/50 hover:text-white transition-colors">
                  <Mail className="w-4 h-4 text-[#00A0D1]" /> info@ugllariano.it
                </a>
                <a href="tel:3514134225" className="flex items-center gap-2.5 text-sm text-white/50 hover:text-white transition-colors">
                  <Phone className="w-4 h-4 text-[#00A0D1]" /> 351 413 4225
                </a>
                <div className="flex items-center gap-2.5 text-sm text-white/50">
                  <MapPin className="w-4 h-4 text-[#00A0D1]" /> Via Roma 119, Lariano (RM)
                </div>
              </div>
            </div>

            {/* Navigazione */}
            <div>
              <h4 className="font-medium text-xs uppercase tracking-[0.2em] text-[#C9A84C] mb-5">Navigazione</h4>
              <div className="space-y-2.5">
                {navLinks.filter(l => !l.isRoute).map(link => (
                  <button key={link.href} onClick={() => scrollTo(link.href)} className="text-sm text-white/50 hover:text-[#00A0D1] transition-colors block">
                    {link.label}
                  </button>
                ))}
                <Link to="/tesseramento" className="text-sm text-[#C9A84C]/70 hover:text-[#C9A84C] transition-colors block font-medium">
                  Tesseramento
                </Link>
              </div>
            </div>

            {/* Link Utili */}
            <div>
              <h4 className="font-medium text-xs uppercase tracking-[0.2em] text-[#C9A84C] mb-5">Link Utili</h4>
              <div className="space-y-2.5">
                <a href="https://www.ugl.it" target="_blank" rel="noopener noreferrer" className="text-sm text-white/50 hover:text-[#00A0D1] transition-colors block">
                  UGL Nazionale
                </a>
                <Link to="/login" className="text-sm text-white/50 hover:text-[#00A0D1] transition-colors block">
                  Area Riservata
                </Link>
                <Link to="/tesseramento" className="text-sm text-white/50 hover:text-[#00A0D1] transition-colors block">
                  Tesseramento
                </Link>
                <a href="https://www.facebook.com/ugllariano" target="_blank" rel="noopener noreferrer" className="text-sm text-white/50 hover:text-[#00A0D1] transition-colors block">
                  Facebook
                </a>
                <a href="https://www.instagram.com/ugl_lariano" target="_blank" rel="noopener noreferrer" className="text-sm text-white/50 hover:text-[#00A0D1] transition-colors block">
                  Instagram
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/8 mt-12 pt-8">
            {/* AES + Page Counter */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#C9A84C]/15 bg-[#C9A84C]/[0.03]">
                <Lock className="w-3.5 h-3.5 text-[#C9A84C]" />
                <p className="text-[10px] font-semibold tracking-widest uppercase text-[#C9A84C]/60">
                  Dati protetti con crittografia AES-256
                </p>
                <Lock className="w-3.5 h-3.5 text-[#C9A84C]" />
              </div>
              {pageViews > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#5ba8d9]/15 bg-[#5ba8d9]/[0.03]">
                  <Eye className="w-3.5 h-3.5 text-[#5ba8d9]" />
                  <p className="text-[10px] font-semibold tracking-widest uppercase text-[#5ba8d9]/60">
                    {pageViews.toLocaleString('it-IT')} visite
                  </p>
                </div>
              )}
            </div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <img src={UGL_LOGO} alt="UGL" className="w-5 h-5 object-contain opacity-60" />
                <p className="text-xs text-white/30">
                  Made in <span className="text-[#C9A84C]/60 font-semibold">UGL - UNIONE ZONALE LARIANO</span> &mdash; Tutti i diritti sono riservati &copy; {new Date().getFullYear()}
                </p>
              </div>
              <div className="flex items-center gap-4 text-xs text-white/30">
                <Link to="/privacy" className="hover:text-white/60 transition-colors" data-testid="privacy-link">
                  Privacy Policy
                </Link>
                <span className="text-white/10">|</span>
                <Link to="/cookie-policy" className="hover:text-white/60 transition-colors" data-testid="cookie-link">
                  Cookie Policy
                </Link>
                <span className="text-white/10">|</span>
                <span>C.F. 95054510581</span>
              </div>
            </div>
            <div className="text-center mt-4">
              <p className="text-[9px] text-white/15">
                v1.3 &mdash; 3 maggio 2026 | &copy; {new Date().getFullYear()} UGL - Unione Zonale Lariano. Tutti i diritti riservati. Proprietà intellettuale protetta ai sensi della L. 633/1941 e successive modifiche.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Cookie Consent Banner */}
      {!cookieAccepted && (
        <div className="fixed bottom-0 left-0 right-0 z-[100] animate-slide-up" data-testid="cookie-banner" role="alert" aria-label="Consenso cookie">
          <div className="bg-[#001830]/95 backdrop-blur-xl border-t border-[#C9A84C]/20 px-6 py-4">
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                <Cookie className="w-5 h-5 text-[#C9A84C] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-white/60 leading-relaxed">
                  Questo sito utilizza cookie tecnici e di profilazione per garantire la migliore esperienza di navigazione. 
                  Continuando a navigare accetti l'utilizzo dei cookie. Per maggiori informazioni consulta la nostra 
                  <span className="text-[#00A0D1] cursor-pointer"> Privacy Policy</span>.
                </p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <Button onClick={acceptCookies} size="sm" className="bg-[#C9A84C] text-[#001830] hover:bg-[#C9A84C]/90 text-xs font-semibold px-5" data-testid="accept-cookies-btn">
                  Accetta
                </Button>
                <Button onClick={acceptCookies} size="sm" variant="outline" className="border-white/20 text-white/60 hover:bg-white/5 text-xs px-4" data-testid="reject-cookies-btn">
                  Rifiuta
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Service Detail Modal */}
      {serviceModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center px-4" onClick={() => setServiceModal(null)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div className="relative bg-[#13243d] border border-[#1e3456] rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-2xl shadow-black/50" onClick={e => e.stopPropagation()} style={{ boxShadow: '0 25px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)' }}>
            {/* Header */}
            <div className="sticky top-0 bg-[#13243d] border-b border-[#1e3456] p-5 flex items-center justify-between z-10 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${serviceModal.gold ? 'bg-[#C9A84C]/15' : 'bg-[#00A0D1]/15'}`}>
                  <serviceModal.icon className={`w-5 h-5 ${serviceModal.gold ? 'text-[#C9A84C]' : 'text-[#00A0D1]'}`} />
                </div>
                <h3 className="text-base font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{serviceModal.title}</h3>
              </div>
              <button onClick={() => setServiceModal(null)} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/50 hover:text-white transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            {/* Body */}
            <div className="p-6">
              <p className="text-sm text-white/60 leading-relaxed mb-5">{serviceModal.detail}</p>
              <div className="space-y-2 mb-6">
                {serviceModal.items.map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg bg-white/[0.02] border border-white/5">
                    <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${serviceModal.gold ? 'bg-[#C9A84C]/15 text-[#C9A84C]' : 'bg-[#00A0D1]/15 text-[#00A0D1]'}`}>
                      <span className="text-[10px] font-bold">{i + 1}</span>
                    </div>
                    <span className="text-sm text-white/70">{item}</span>
                  </div>
                ))}
              </div>
              <a href={`https://wa.me/393514134225?text=${encodeURIComponent(`Salve, vorrei informazioni sul servizio: ${serviceModal.title}`)}`} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-[#25D366] text-white font-semibold text-sm hover:bg-[#25D366]/90 transition-colors">
                <MessageCircle className="w-4 h-4" /> {serviceModal.cta}
              </a>
              <p className="text-center text-[10px] text-white/20 mt-3">UGL - Unione Zonale Lariano | Via Roma 119</p>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Floating Button */}
      <a href="https://wa.me/393514134225?text=Salve%2C%20vorrei%20informazioni%20sui%20servizi%20UGL%20Lariano"
        target="_blank" rel="noopener noreferrer" data-testid="whatsapp-btn" aria-label="Contattaci su WhatsApp"
        className="whatsapp-btn fixed bottom-6 right-6 z-[90] w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center shadow-lg shadow-[#25D366]/30 hover:scale-110 hover:shadow-xl hover:shadow-[#25D366]/40 transition-all duration-300">
        <MessageCircle className="w-6 h-6 text-white" aria-hidden="true" />
      </a>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} data-testid="scroll-top-btn" aria-label="Torna in cima alla pagina"
          className="fixed bottom-6 left-6 z-[90] w-11 h-11 rounded-full bg-[#13243d] border border-[#1e3456] flex items-center justify-center shadow-lg shadow-black/30 hover:bg-[#1a3a5c] hover:border-[#2a5080] hover:-translate-y-1 transition-all duration-300">
          <ChevronUp className="w-5 h-5 text-[#5ba8d9]" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
